import { ResponseError } from 'sos-common/js/utils/ResponseError';
import { getFormConfig } from 'sos-eservice-common/js/config';

const JSON_PARSE_FAIL = 'Failed to parse JSON of server response';

function buildPath(path: string) {
  const config = getFormConfig();
  if (config) return `${config.basePath}${path}`;
  return path;
}

function properReturnValueFromFetchResult<T>(
  text: string,
  response: Response,
  type: string,
  path: string
): T | ResponseError {
  if (response.ok) {
    try {
      return JSON.parse(text) as T;
    } catch {
      const jsonFail = new ResponseError(JSON_PARSE_FAIL, response);
      jsonFail.body = { errorMessage: JSON_PARSE_FAIL };
      return jsonFail;
    }
  } else {
    const error = new ResponseError(`Error on Http.${type} to ${path}`, response);

    try {
      error.body = JSON.parse(text);
    } catch {
      // 404.13 = Exceeds MaxRequestLength
      if (error.statusCode === 404 && error.body.search('404.13')) {
        error.body = { errorMessage: 'Error: 404.13: Filen du försöker ladda upp är för stor.' };
      } else {
        error.body = { errorMessage: `Ett fel inträffade: ${response.status} ${response.statusText}` };
      }
    }
    return error;
  }
}

export function doPost<T>(path: string, body: any, headers?: Headers): Promise<T | ResponseError> {
  return fetch(buildPath(path), {
    body,
    headers,
    credentials: 'same-origin',
    method: 'POST',
  })
    .then((response) => {
      return Promise.all([response.text(), response]);
    })
    .then((arr) => {
      const text = arr[0];
      const response = arr[1];
      return properReturnValueFromFetchResult<T>(text, response, 'POST', path);
    });
}

export function doPatch<T>(path: string, body: any, headers?: Headers): Promise<T | ResponseError> {
  return fetch(buildPath(path), {
    body,
    headers,
    credentials: 'same-origin',
    method: 'PATCH',
  })
    .then((response) => {
      return Promise.all([response.text(), response]);
    })
    .then((arr) => {
      const text = arr[0];
      const response = arr[1];
      return properReturnValueFromFetchResult<T>(text, response, 'PATCH', path);
    });
}

export function doDelete<T>(path: string, body?: any, headers?: Headers): Promise<T | ResponseError> {
  return fetch(buildPath(path), {
    body,
    headers,
    credentials: 'same-origin',
    method: 'DELETE',
  })
    .then((response) => {
      return Promise.all([response.text(), response]);
    })
    .then((arr) => {
      const text = arr[0];
      const response = arr[1];
      return properReturnValueFromFetchResult<T>(text, response, 'DELETE', path);
    });
}

export function doGet<T>(path: string, headers?: Headers): Promise<T | ResponseError> {
  return fetch(buildPath(path), {
    headers,
    method: 'GET',
    credentials: 'same-origin',
  })
    .then((response) => {
      return Promise.all([response.text(), response]);
    })
    .then((arr) => {
      const text = arr[0];
      const response = arr[1];
      return properReturnValueFromFetchResult<T>(text, response, 'GET', path);
    });
}
